import type { PlayerInput } from "@arcade-hockey/shared";
import { SEAT_IDS } from "../state/types.js";
import type { MatchState, PlayerState, TeamControlState } from "../state/types.js";

function seatIsAssigned(team: TeamControlState, seatId: string): boolean {
  for (const k of SEAT_IDS) {
    if (k === seatId && team.controlledByHuman[k] !== null) return true;
  }
  return false;
}

function playerForSeat(team: TeamControlState, seatId: string): string | null {
  for (const k of SEAT_IDS) {
    if (k === seatId) return team.controlledByHuman[k];
  }
  return null;
}

export function findTeamForSeat(state: MatchState, seatId: string): TeamControlState | null {
  for (const team of state.control) {
    if (seatIsAssigned(team, seatId)) return team;
  }
  return null;
}

export function getControlledPlayer(state: MatchState, seatId: string): PlayerState | null {
  const team = findTeamForSeat(state, seatId);
  if (team === null) return null;
  const id = playerForSeat(team, seatId);
  if (id === null) return null;
  return state.playersById.get(id as never) ?? null;
}

export function findTeamByPlayer(state: MatchState, playerId: string): TeamControlState | null {
  const player = state.playersById.get(playerId as never);
  if (player === undefined) return null;
  return state.control.find((t) => t.teamId === player.teamId) ?? null;
}

export function findSeatForPlayer(team: TeamControlState, playerId: string): string | undefined {
  for (const k of SEAT_IDS) {
    if (team.controlledByHuman[k] === playerId) return k;
  }
  return undefined;
}

// Resolve a PlayerInput to its acting player. Bots set `input.playerId`
// directly so each non-human skater can act independently in the same
// tick; human inputs leave it null and the seat assignment is the only
// route. Phase G push 2 fix: stick-lift and poke handlers had been
// looking up `input.seatId` only, so bot defenders pressing StickLift
// were resolving to the human's seat player and the strip silently
// no-op'd. Use this helper from any action handler that needs "who
// pressed the button" — human OR bot.
export function findActor(state: MatchState, input: PlayerInput): PlayerState | null {
  if (input.playerId !== null) {
    return state.playersById.get(input.playerId) ?? null;
  }
  return getControlledPlayer(state, input.seatId);
}
